<template>
  <section class="services-section">

    <!-- TITLE -->
    <div class="title-wrapper">
      <h1 class="title">Nuestros Servicios</h1>
      <p class="subtitle">
        Soluciones digitales diseñadas para impulsar el crecimiento de tu negocio.
      </p>
    </div>

    <!-- GRID -->
    <div class="services-container">

      <article
        v-for="(service, index) in services"
        :key="service.id"
        class="service-card-wrapper"
        :style="{ animationDelay: index * 120 + 'ms' }"
      >

        <router-link
          :to="service.route"
          class="service-card"
          :aria-label="service.ariaLabel"
        >

          <!-- IMAGE -->
          <div class="service-image">
            <img
              :src="service.image"
              :alt="service.alt"
              loading="lazy"
              decoding="async"
              width="600"
              height="400"
              @error="handleImageError"
            >
          </div>

          <!-- CONTENT -->
          <div class="card-content">
            <h2>{{ service.title }}</h2>
            <span class="cta">Explorar servicio →</span>
          </div>

        </router-link>

      </article>

    </div>

  </section>
</template>

<script>
import { useHead } from "@vueuse/head";

export default {
  name: "ServiceComponent",

  setup() {
    useHead({
      title: "Servicios de TI | Raptoris",
      htmlAttrs: { lang: "es-MX" },
      meta: [
        { charset: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        {
          name: "description",
          content:
            "Explora nuestros servicios de gestión digital, ventas online, soporte TI y branding en México."
        },
        { name: "robots", content: "index, follow" },
        { property: "og:title", content: "Servicios de TI | Raptoris" },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "https://raptoris.com/images/servicios-preview.jpg" }
      ],
      link: [
        { rel: "canonical", href: "https://raptoris.com/servicios" }
      ]
    });
  },

  data() {
    return {
      services: [
        {
          id: 1,
          title: "Gestión y Organización Digital",
          route: "/servicios/gestion-organizacion-digital",
          image: "/images/img10.jpg",
          alt: "Servicio de gestión y organización digital",
          ariaLabel: "Explorar servicio de gestión y organización digital"
        },
        {
          id: 2,
          title: "Implementación de Ventas Digitales",
          route: "/servicios/ventas-digitales",
          image: "/images/img11.jpg",
          alt: "Servicio de implementación de ventas digitales",
          ariaLabel: "Explorar servicio de implementación de ventas digitales"
        },
        {
          id: 3,
          title: "Soporte y Optimización Continua",
          route: "/servicios/soporte-optimizacion-continua",
          image: "/images/img1.jpg",
          alt: "Servicio de soporte y optimización continua",
          ariaLabel: "Explorar servicio de soporte y optimización continua"
        },
        {
          id: 4,
          title: "Branding e Identidad de Marca",
          route: "/servicios/branding-identidad-marca",
          image: "/images/img18.jpg",
          alt: "Servicio de branding e identidad de marca",
          ariaLabel: "Explorar servicio de branding e identidad de marca"
        }
      ]
    };
  },

  methods: {
    handleImageError(event) {
      event.target.src = "/images/fallback-image.jpg";
    }
  }
};
</script>

<style scoped>

/* ===== SECTION ===== */
.services-section {
  padding: 6rem 2.5rem;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

/* ===== TITLE ===== */
.title-wrapper {
  text-align: center;
  margin-bottom: 4rem;
  animation: fadeInDown 0.8s ease forwards;
  opacity: 0;
}

.title {
  font-size: 2.8rem;
  font-weight: 300;
  letter-spacing: 3px;
  color: #0f172a;
}

.subtitle {
  margin-top: 1rem;
  color: #64748b;
  font-size: 1.05rem;
}

/* ===== GRID ===== */
.services-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;
  align-items: stretch;
}

/* ===== WRAPPER ===== */
.service-card-wrapper {
  display: flex;
  animation: fadeUp 0.7s ease forwards;
  opacity: 0;
}

/* ===== CARD (UNIFORM FIX) ===== */
.service-card {
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 380px; /* 🔥 clave de uniformidad */

  text-decoration: none;

  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  overflow: hidden;

  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);

  transition: all 0.35s ease;
}

/* HOVER */
.service-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.12);
}

/* ===== IMAGE ===== */
.service-image {
  height: 200px;
  flex-shrink: 0;
  overflow: hidden;
}

.service-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s ease;
}

.service-card:hover img {
  transform: scale(1.06);
}

/* ===== CONTENT ===== */
.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1.5rem;
}

/* TITLE FIX UNIFORM */
.service-card h2 {
  font-size: 1.05rem;
  font-weight: 500;
  color: #0f172a;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  min-height: 2.6em;
}

/* CTA */
.cta {
  font-size: 0.8rem;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #065f46;
  transition: 0.3s;
  margin-top: auto;
}

.service-card:hover .cta {
  color: #10b981;
  transform: translateX(4px);
}

/* ===== ANIMATIONS ===== */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(25px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fadeInDown {
  from { opacity: 0; transform: translateY(-15px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== MOBILE ===== */
@media (max-width: 768px) {
  .services-section {
    padding: 4rem 1.2rem;
  }

  .title {
    font-size: 2rem;
  }

  .service-card {
    height: 360px;
  }
}

</style>